/* "No seu dia" (sections/e-ritual.liquid).
   Fica aceso o primeiro capítulo cujo cartão ainda não passou da linha de
   troca: a cena dele, o relógio e a luz do céu (data-tone). A linha vem do
   CSS (--e-ritual-cue, % da altura do palco): no celular é o horizonte, e o
   cartão apaga ao cruzá-lo, sem cobrir a cena; no desktop é o topo do palco.
   Cartão mais alto que o espaço abaixo da linha (texto ampliado a 200%, por
   exemplo): ele cobre a cena de qualquer jeito, então fica aceso até o pé
   dele cruzar a linha; pelo topo, apagaria assim que começasse a subir.
   A rolagem da história leva o sol pelo arco: --day, gravado só no sol, vai
   de 0 (horizonte da manhã) a 1 (horizonte do fim do dia). Com movimento
   reduzido o sol não desliza: fica na posição do capítulo aceso.
   A rolagem só é ouvida com a seção na tela. Cada frame lê todas as medidas
   antes de escrever. */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class ERitual extends HTMLElement {
  connectedCallback() {
    this.story = this.querySelector('[data-ritual-story]');
    this.stage = this.querySelector('[data-ritual-stage]');
    this.sun = this.querySelector('.e-ritual__sun');
    this.chapters = [...this.querySelectorAll('[data-ritual-chapter]')];
    this.cards = [...this.querySelectorAll('[data-ritual-card]')];
    this.scenes = [...this.querySelectorAll('[data-ritual-scene]')];
    if (!this.story || !this.stage || this.cards.length === 0) return;

    this.readLayout();
    this.observer = new IntersectionObserver(this.handleVisibility);
    this.observer.observe(this.story);
    window.addEventListener('resize', this.handleResize, { passive: true });
    reducedMotion.addEventListener('change', this.requestUpdate);
    this.update();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    window.removeEventListener('scroll', this.requestUpdate);
    window.removeEventListener('resize', this.handleResize);
    reducedMotion.removeEventListener('change', this.requestUpdate);
    cancelAnimationFrame(this.frame);
  }

  /* Várias mudanças podem chegar juntas (sai e volta rápido): vale a última. */
  handleVisibility = (entries) => {
    const entry = entries[entries.length - 1];
    if (entry.isIntersecting) {
      window.addEventListener('scroll', this.requestUpdate, { passive: true });
      this.requestUpdate();
    } else {
      window.removeEventListener('scroll', this.requestUpdate);
    }
  };

  handleResize = () => {
    this.readLayout();
    this.requestUpdate();
  };

  /* A linha de troca e o respiro do cartão preso ao pé (bottom do último),
     que decide se um cartão é alto demais para caber abaixo da linha. */
  readLayout() {
    this.cue = (Number.parseFloat(getComputedStyle(this).getPropertyValue('--e-ritual-cue')) || 0) / 100;
    this.cardGap = Number.parseFloat(getComputedStyle(this.cards[this.cards.length - 1]).bottom) || 0;
  }

  requestUpdate = () => {
    if (!this.frame) this.frame = requestAnimationFrame(this.update);
  };

  update = () => {
    this.frame = 0;
    const stage = this.stage.getBoundingClientRect();
    const story = this.story.getBoundingClientRect();
    const cardBoxes = this.cards.map((card) => card.getBoundingClientRect());
    const line = stage.top + stage.height * this.cue;
    const upcoming = cardBoxes.findIndex((box) => {
      const tall = stage.bottom - this.cardGap - box.height < line;
      return (tall ? box.bottom : box.top) >= line;
    });
    const index = upcoming === -1 ? this.cards.length - 1 : upcoming;
    const travel = story.height - window.innerHeight;
    const progress = travel > 0 ? Math.min(1, Math.max(0, -story.top / travel)) : 0;
    const steps = this.cards.length - 1;
    const day = (reducedMotion.matches && steps > 0 ? index / steps : progress).toFixed(4);

    this.activate(index);
    if (day !== this.day) {
      this.day = day;
      this.sun.style.setProperty('--day', day);
    }
  };

  activate(index) {
    if (index === this.activeIndex) return;
    this.activeIndex = index;
    this.dataset.tone = this.chapters[index].dataset.tone;
    this.chapters.forEach((chapter, position) => chapter.toggleAttribute('data-active', position === index));
    this.scenes.forEach((scene, position) => scene.toggleAttribute('data-active', position === index));
  }
}

if (!customElements.get('e-ritual')) customElements.define('e-ritual', ERitual);
